/**
 * coachAdvance.ts — DOM listeners that let a step advance itself, besides
 * the card's own Next button. No React: CoachMarkHost owns the lifecycle
 * (attach on step change, run the returned cleanup on the next change and
 * on unmount).
 *
 *   click  — capture-phase, once, on the anchor element. Capture beats any
 *            app handler on a descendant that calls stopPropagation() in
 *            the bubble phase — the tour has to see the click regardless of
 *            what the app does with it afterwards.
 *   input  — capture-phase 'input' on the anchor (works whether the anchor
 *            is the field itself or a wrapper around one), debounced so a
 *            step doesn't advance mid-keystroke.
 *   event  — a `founding:coach` CustomEvent with a matching `detail.name`.
 *            The only kind that works with `el === null` — some events
 *            (a build finishing) have no on-screen control to listen on.
 */

import { COACH_EVENT, type CoachEventDetail } from './coachEvents';
import type { CoachAdvance } from './coachTypes';

const DEFAULT_INPUT_DELAY_MS = 600;

const NOOP = () => {};

export function attachAdvanceListener(
  el: Element | null,
  advance: CoachAdvance | undefined,
  onAdvance: () => void,
  opts?: { win?: Window; inputDelayMs?: number },
): () => void {
  if (!advance) return NOOP;

  if (advance.kind === 'click') {
    if (!el) return NOOP;
    const handler = () => onAdvance();
    el.addEventListener('click', handler, { capture: true, once: true });
    return () => el.removeEventListener('click', handler, true);
  }

  if (advance.kind === 'input') {
    if (!el) return NOOP;
    const delay = opts?.inputDelayMs ?? DEFAULT_INPUT_DELAY_MS;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const handler = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        onAdvance();
      }, delay);
    };
    el.addEventListener('input', handler, { capture: true });
    return () => {
      el.removeEventListener('input', handler, true);
      if (timer) clearTimeout(timer);
    };
  }

  // advance.kind === 'event'
  const win = opts?.win ?? (typeof window !== 'undefined' ? window : undefined);
  if (!win) return NOOP;
  const name = advance.name;
  const handler = (e: Event) => {
    const detail = (e as CustomEvent<CoachEventDetail>).detail;
    if (!detail || detail.name !== name) return;
    win.removeEventListener(COACH_EVENT, handler);
    onAdvance();
  };
  win.addEventListener(COACH_EVENT, handler);
  return () => win.removeEventListener(COACH_EVENT, handler);
}
